import React, { useEffect, useState } from "react";
import { DialogBox } from "../../shared/components/dialog";
import {
  Autocomplete,
  Box,
  Checkbox,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  phoneRegex,
  emailRegex,
  pinRegex,
  addressRegex,
  nameRegex,
} from "../../shared/util/authentication.regex";
import { useArcAuth } from "@arcauth/react-sdk";
import { RootStateType } from "../../redux/features/rootSlice";
import {
  getCountriesApi,
  getStatesFromApi,
} from "../../redux/features/country.slice";
import { addAddress, updateAddress } from "../../redux/features/address.slice";
import { getUserInfo } from "../../redux/features/user.slice";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  address?: AddressDetails;
  isEditOpen: boolean;
}

interface AddressDetails {
  id: string | null;
  name: string | null;
  street2: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  addressname: string | null;
  isdefault: boolean;
  companyname: string | null;
}

const AddAddress = ({open, onClose, address, isEditOpen}: DialogProps) => {
  const dispatch = useDispatch();
  const [addressDetails, setAddressDetails] = useState<AddressDetails>({
    id: null,
    name: null,
    street2: "Business",
    city: null,
    state: null,
    zip: null,
    country: null,
    phone: null,
    email: null,
    addressname: null,
    isdefault: false,
    companyname: null,
  });

  const [selectedCountry, setSelectedCountry] = useState<any>();
  const [selectedState, setSelectedState] = useState<any>();
  const [isEmailTouched, setIsEmailTouched] = useState(false);
  const [emailHelperText, setEmailHelperText] = useState("");
  const [isPinTouched, setIsPinTouched] = useState(false);
  const [pinHelperText, setPinHelperText] = useState("");
  const [isPhoneTouched, setIsPhoneTouched] = useState(false);
  const [phoneHelperText, setPhoneHelperText] = useState("");
  const [isAddressTouched, setIsAddressTouched] = useState(false);
  const [addressHelperText, setAddressHelperText] = useState("");
  const [isNameTouched, setIsNameTouched] = useState(false);
  const [nameHelperText, setNameHelperText] = useState("");
  const [isCityTouched, setIsCityTouched] = useState(false);
  const [location, setLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);

  const { countries, states }: any = useSelector(
    (state: RootStateType) => state.countryStateReducer
  );

  const addressUpdate_loading = useSelector(
    (state: RootStateType) => state.addressStateReducer.loading
  );

  useEffect(() => {
    setAddressDetails({
      id: address?.id || null,
      name: address?.name || null,
      street2: address?.street2 || "Business",
      city: address?.city || null,
      state: address?.state || null,
      zip: address?.zip || null,
      country: address?.country || null,
      phone: address?.phone || null,
      email: address?.email || null,
      addressname: address?.addressname || null,
      isdefault: address?.isdefault || false,
      companyname: address?.companyname || "",
    });
    setSelectedCountry({
      label: address?.country || "",
      value: 0,
    });
    setSelectedState({
      StateID: "",
      StateName: address?.state || "",
    });
  }, [address]);

  const clearForm = () => {
    setIsEmailTouched(false);
    setEmailHelperText("");
    setIsPinTouched(false);
    setPinHelperText("");
    setIsPhoneTouched(false);
    setPhoneHelperText("");
    setIsAddressTouched(false);
    setAddressHelperText("");
    setIsNameTouched(false);
    setNameHelperText("");
    setIsCityTouched(false);

    setSelectedCountry({
      label: "",
      value: null,
    });
    setSelectedState({
      StateID: "",
      StateName: null,
    });
  };

  const dialogClose = () => {
    clearForm();
    onClose();
  };

  const handleListItemClick = async () => {
    const dispatchObj = {
      id: addressDetails.id,
      address: {
        name: addressDetails.name,
        street2: addressDetails.street2,
        city: addressDetails.city,
        state: addressDetails.state,
        zip: addressDetails.zip,
        country: addressDetails.country,
        phone: addressDetails.phone,
        email: addressDetails.email,
        addressname: addressDetails.addressname,
        isdefault: addressDetails.isdefault,
        companyname: addressDetails.companyname,
      },
      callBackSuccess: () => {
        onClose();
        dispatch(getUserInfo());
        clearForm();
      },
    };
    if (!isEditOpen) {
      dispatch(addAddress(dispatchObj));
    } else {
      dispatch(updateAddress(dispatchObj));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isFormValid()) {
      e.preventDefault();
      handleListItemClick();
    }
  };

  const handleChangecountry = () => {
    dispatch(getStatesFromApi(selectedCountry?.value));
    setSelectedState({
      StateID: "",
      StateName: null,
    });
  };

  const checkPhone = (phone: string) => {
    setIsPhoneTouched(true);
    if (isPhoneTouched && phone.length < 1) {
      setPhoneHelperText("Phone cannot be empty");
    } else if (!phoneRegex.test(phone)) {
      setPhoneHelperText("Invalid phone number");
    } else {
      setPhoneHelperText("");
    }
  };

  const checkEmail = (email: string) => {
    setIsEmailTouched(true);
    if (isEmailTouched && email.length < 1) {
      setEmailHelperText("Email cannot be empty");
    } else if (!emailRegex.test(email)) {
      setEmailHelperText("Invalid email");
    } else {
      setEmailHelperText("");
    }
  };

  const checkPin = (pin: string) => {
    setIsPinTouched(true);
    if (isPinTouched && pin.length < 1) {
      setPinHelperText("Pin cannot be empty");
    } else if (!pinRegex.test(pin)) {
      setPinHelperText("Invalid pin");
    } else {
      setPinHelperText("");
    }
  };

  const checkAddress = (address: string) => {
    setIsAddressTouched(true);
    if (isAddressTouched && address.length < 1) {
      setAddressHelperText("Address cannot be empty");
    } else if (!addressRegex.test(address)) {
      setAddressHelperText("Invalid address");
    } else {
      setAddressHelperText("");
    }
  };

  const checkName = (name: string) => {
    setIsNameTouched(true);
    if (isNameTouched && name.length < 1) {
      setNameHelperText("Name cannot be empty");
    } else if (!nameRegex.test(name)) {
      setNameHelperText("Invalid name");
    } else {
      setNameHelperText("");
    }
  };

  const isFormValid = () => {
    const { name, city, state, zip, country, phone, email, addressname } =
      addressDetails;
    const requiredFields = [
      name,
      city,
      state,
      zip,
      country,
      phone,
      email,
      addressname,
    ];
    const areRequiredFieldsFilled = requiredFields.every((field) => field);
    return (
      areRequiredFieldsFilled &&
      !emailHelperText &&
      !pinHelperText &&
      !phoneHelperText &&
      !addressHelperText &&
      !nameHelperText
    );
  };

  useEffect(() => {
    
    setTimeout(() => {
      dispatch(getCountriesApi());
    }, 300);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({ latitude, longitude });
          //fetchAddress(latitude, longitude);
        },
        (error) => {
          console.error("Error getting geolocation:", error);
        }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  }, []);

  const fetchAddress = async (latitude: number, longitude: number) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=API_KEY`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch address");
      }
      const data = await response.json();
      const formattedAddress = data.results[0]?.formatted_address;
      const addressComponents = data.results[0]?.address_components;

      // Extract address details
      const cityComponent = addressComponents?.find((component: any) =>
        component.types.includes("locality")
      );
      const stateComponent = addressComponents?.find((component: any) =>
        component.types.includes("administrative_area_level_1")
      );
      const countryComponent = addressComponents?.find((component: any) =>
        component.types.includes("country")
      );
      const postalCodeComponent = addressComponents?.find((component: any) =>
        component.types.includes("postal_code")
      );
      const sublocalityComponent = addressComponents?.find((component: any) =>
        component.types.includes("sublocality")
      );
      const sublocality1Component = addressComponents?.find((component: any) =>
        component.types.includes("sublocality_level_2")
      );
      const sublocality2Component = addressComponents?.find((component: any) =>
        component.types.includes("sublocality_level_1")
      );

      const city = cityComponent ? cityComponent.long_name : null;
      const state = stateComponent ? stateComponent.long_name : null;
      const country = countryComponent ? countryComponent.long_name : null;
      const zip = postalCodeComponent ? postalCodeComponent.long_name : null;
      const addressname = sublocalityComponent
        ? sublocalityComponent.long_name
        : null + sublocality1Component
        ? sublocality1Component.long_name
        : null + sublocality2Component
        ? sublocality2Component.long_name
        : null;

    } catch (error) {
      console.error("Error fetching address:", error);
    }
  };

  // selected value on text field
  //   const [addressvalue, setAddressValue] = React.useState(addressDetails.sublocality);
  //   const [countryvalue, setCountryValue] = React.useState(addressDetails.country || "");

  return (
    <>
      <DialogBox
        onClose={dialogClose}
        title={isEditOpen ? "Edit address" : "Add address"}
        buttonText={"Submit"}
        isLoading={addressUpdate_loading}
        buttonClick={handleListItemClick}
        open={open}
        isBtnEnabled={!isFormValid()}
      >
        <DialogContent sx={{ py: 0 }}>
          <Box flex={1} data-testid="Add-new-Address-box-name">
            <TextField
              label="Full name"
              autoFocus
              id="FullName"
              type="text"
              fullWidth
              variant="outlined"
              placeholder=""
              size="small"
              name="name"
              color="secondary"
              inputProps={{ maxLength: 60 }}
              required={true}
              onKeyDown={handleKeyDown}
              value={addressDetails?.name}
              onChange={(e) => {
                checkName(e.target.value);
                setAddressDetails({
                  ...addressDetails,
                  name: e.target.value,
                });
              }}
            />
            <FormHelperText
              style={{
                color: "red",
              }}
            >
              {nameHelperText}
            </FormHelperText>
          </Box>
          <Box flex={1} mt={2} data-testid="Add-new-Address-box-phoneno">
            <TextField
              label="Company Name"
              id="CompanyName"
              type="text"
              fullWidth
              variant="outlined"
              placeholder=""
              size="small"
              name="CompanyName"
              color="secondary"
              inputProps={{ maxLength: 150 }}
              onKeyDown={handleKeyDown}
              value={addressDetails?.companyname}
              onChange={(e) =>
                setAddressDetails({
                  ...addressDetails,
                  companyname: e.target.value,
                })
              }
            />
          </Box>
          <Stack direction={"row"} gap={2}>
            <Box flex={1} mt={2} data-testid="Add-new-Address-box-phoneno">
              <TextField
                label="Phone number"
                id="PhoneNumber"
                type="text"
                fullWidth
                variant="outlined"
                placeholder=""
                size="small"
                name="phone"
                color="secondary"
                inputProps={{ maxLength: 20 }}
                required={true}
                onKeyDown={handleKeyDown}
                value={addressDetails?.phone}
                onChange={(e) => {
                  checkPhone(e.target.value);
                  setAddressDetails({
                    ...addressDetails,
                    phone: e.target.value,
                  });
                }}
              />
              <FormHelperText
                style={{
                  color: "red",
                }}
              >
                {phoneHelperText}
              </FormHelperText>
            </Box>
            <Box flex={1} mt={2} data-testid="Add-new-Address-box-email">
              <TextField
                label="Email id"
                id="EmailID"
                type="text"
                fullWidth
                variant="outlined"
                placeholder=""
                size="small"
                name="email"
                color="secondary"
                inputProps={{ maxLength: 100 }}
                required={true}
                onKeyDown={handleKeyDown}
                value={addressDetails?.email}
                onChange={(e) => {
                  checkEmail(e.target.value);

                  setAddressDetails({
                    ...addressDetails,
                    email: e.target.value,
                  });
                }}
              />

              <FormHelperText
                style={{
                  color: "red",
                }}
              >
                {emailHelperText}
              </FormHelperText>
            </Box>
          </Stack>

          <Stack mt={2} justifyContent={"space-between"}>
            <TextField
              className="selectaddress"
              id="outlined-basic"
              label="Address"
              variant="outlined"
              size="small"
              color="secondary"
              required={true}
              value={addressDetails?.addressname}
              onKeyDown={handleKeyDown}
              onChange={(e) => {
                checkAddress(e.target.value);
                setAddressDetails({
                  ...addressDetails,
                  addressname: e.target.value,
                });
              }}
            />
            <FormHelperText
              style={{
                color: "red",
              }}
            >
              {addressHelperText}
            </FormHelperText>

            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent={"space-between"}
              gap={2}
            >
              {/* -------------/.- */}
              <Autocomplete
                id="country-select-demo"
                sx={{ mt: 1, mb: 0.5, flex: 1 }}
                options={countries}
                value={selectedCountry}
                onChange={(a: any, value: any) => {
                  if (value === null) {
                    setSelectedCountry({
                      label: "",
                      value: null,
                    });
                    setSelectedState({
                      StateID: "",
                      StateName: null,
                    });
                  } else {
                    setAddressDetails({
                      ...addressDetails,
                      country: value?.label,
                    });
                    setSelectedCountry({
                      label: value?.label,
                      value: value?.value,
                    });
                    setSelectedState({
                      StateName: "",
                      StateID: null,
                    });
                  }
                }}
                autoHighlight
                filterOptions={(options, { inputValue }) =>
                  options.filter((option) =>
                    option.label
                      ?.toLowerCase()
                      .startsWith(inputValue?.toLowerCase())
                  )
                }
                getOptionLabel={(option) => {
                  return option?.label !== null ? option?.label : "";
                }}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    data-testid="ssarchive-collection-country-name-detail"
                  >
                    {option.label}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    required
                    onKeyDown={handleKeyDown}
                    onChange={() =>
                      setSelectedState({
                        StateName: "",
                        StateID: null,
                      })
                    }
                    fullWidth
                    placeholder="Select country"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "new-password",
                    }}
                  />
                )}
              />

              <Autocomplete
                disabled={selectedCountry?.label ? false : true}
                // loading={statelistLoading}
                id="state-select-demo"
                sx={{ mt: 1, mb: 0.5, flex: 1 }}
                options={states}
                value={selectedState}
                onChange={(a: any, value: any) => {
                  if (value === null) {
                    setSelectedState({ StateName: "", StateID: null });
                  } else {
                    setAddressDetails({
                      ...addressDetails,
                      state: value?.StateName,
                    });
                    setSelectedState({
                      StateName: value?.StateName,
                      StateID: value?.StateID,
                    });
                  }
                }}
                onFocus={handleChangecountry}
                autoHighlight
                filterOptions={(options, { inputValue }) =>
                  options.filter((option) =>
                    option.StateName.toLowerCase().startsWith(
                      inputValue.toLowerCase()
                    )
                  )
                }
                getOptionLabel={(option) =>
                  states?.length > 0 ? option.StateName : ""
                }
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    data-testid="ssarchive-collection-create-state-name-box"
                  >
                    {option.StateName}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    // value={stateinput}
                    size="small"
                    fullWidth
                    required
                    onKeyDown={handleKeyDown}
                    placeholder="Select state"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "new-password", // disable autocomplete and autofill
                    }}
                  />
                )}
              />
            </Stack>
            <Stack
              sx={{ mb: 2 }}
              direction={{ xs: "column", md: "row" }}
              justifyContent={"space-between"}
              gap={2}
            >
              <FormControl
                sx={{ flex: 1, mt: 2 }}
                // size="small"
                // className="selectaddress"
              >
                {/* <InputLabel id="demo-simple-select-label">City</InputLabel> */}
                <TextField
                  className="selectaddress"
                  id="outlined-basic"
                  label="City"
                  // onChange={handleChangecountry}
                  variant="outlined"
                  size="small"
                  color="secondary"
                  required={true}
                  value={addressDetails.city}
                  onKeyDown={handleKeyDown}
                  onChange={(e) => {
                    setIsCityTouched(true);
                    setAddressDetails({
                      ...addressDetails,
                      city: e.target.value,
                    });
                  }}
                />
                <FormHelperText
                  style={{
                    color: "red",
                  }}
                >
                  {!addressDetails?.city && isCityTouched
                    ? "City name cannot be empty"
                    : ""}
                </FormHelperText>
              </FormControl>

              <FormControl sx={{ flex: 1, mt: 2 }}>
                <TextField
                  className="selectaddress"
                  id="outlined-basic"
                  label="Pincode"
                  variant="outlined"
                  size="small"
                  color="secondary"
                  required={true}
                  value={addressDetails.zip}
                  onKeyDown={handleKeyDown}
                  onChange={(e) => {
                    checkPin(e.target.value);
                    setAddressDetails({
                      ...addressDetails,
                      zip: e.target.value,
                    });
                  }}
                />
                <FormHelperText
                  style={{
                    color: "red",
                  }}
                >
                  {pinHelperText}
                </FormHelperText>
              </FormControl>
            </Stack>
            <Box>
              <FormControl>
                <FormLabel id="demo-row-radio-buttons-group-label" required>
                  Type of address
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="demo-row-radio-buttons-group-label"
                  name="row-radio-buttons-group"
                  value={
                    addressDetails.street2 ? addressDetails.street2 : "Business"
                  }
                  onChange={(e) =>
                    setAddressDetails({
                      ...addressDetails,
                      street2: e.target.value,
                    })
                  }
                >
                  <FormControlLabel
                    value="Business"
                    control={<Radio />}
                    label="Business"
                  />
                  <FormControlLabel
                    value="Home"
                    control={<Radio />}
                    label="Home"
                  />
                  <FormControlLabel
                    value="other"
                    control={<Radio />}
                    label="Other"
                  />
                </RadioGroup>
              </FormControl>
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={addressDetails.isdefault}
                    onChange={(e) =>
                      setAddressDetails({
                        ...addressDetails,
                        isdefault: e.target.checked,
                      })
                    }
                  />
                }
                label="Make this my default address"
              />
            </Box>
          </Stack>
        </DialogContent>
      </DialogBox>
    </>
  );
};

export default AddAddress;
